import { MoneyValue } from './MoneyValue';
import { cn } from '@/lib/utils';
import type { SaleQuote } from '@/api/sale.api';

interface QuoteDiscountRowsProps {
  /** The live quote, or null when it is unavailable (loading / failed). */
  quote: SaleQuote | null;
  /** Shown as one "Diskon" row when there is no quote (the local coupon saving). */
  fallbackSavings: number;
  className?: string;
}

/**
 * One row per discount the quote applies, named — the cashier can tell the
 * customer which promo they got and how much each took off. Without a quote,
 * falls back to the single local "Diskon" row the summaries always had.
 */
export function QuoteDiscountRows({ quote, fallbackSavings, className }: QuoteDiscountRowsProps) {
  const rows = quote
    ? quote.discounts.length > 0
      ? quote.discounts.map((d, i) => ({
          key: `${d.discountId ?? d.couponCode ?? 'd'}-${i}`,
          label: d.couponCode ? `${d.name} (${d.couponCode})` : d.name,
          amount: d.amount,
        }))
      : quote.totalDiscount > 0
        ? [{ key: 'total', label: 'Diskon', amount: quote.totalDiscount }]
        : []
    : fallbackSavings > 0
      ? [{ key: 'local', label: 'Diskon', amount: fallbackSavings }]
      : [];

  if (rows.length === 0) return null;
  // With several discounts, their sum on its own line. The quote's own total
  // when there is one: it is what subtotal − total equals.
  const discountTotal =
    rows.length > 1 ? (quote?.totalDiscount ?? rows.reduce((sum, r) => sum + r.amount, 0)) : null;
  return (
    <>
      {rows.map((r) => (
        <div key={r.key} className={cn('flex justify-between gap-3 text-success', className)}>
          <span className="min-w-0 truncate" title={r.label}>
            {r.label}
          </span>
          <span className="shrink-0">
            − <MoneyValue value={r.amount} className="text-success" />
          </span>
        </div>
      ))}
      {discountTotal !== null && (
        <div
          className={cn(
            'flex justify-between gap-3 border-t border-dashed border-success/40 pt-1.5 font-semibold text-success',
            className,
          )}
        >
          <span>Total diskon</span>
          <span className="shrink-0">
            − <MoneyValue value={discountTotal} className="text-success" />
          </span>
        </div>
      )}
    </>
  );
}
